import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Loader2, DollarSign, TrendingUp, CreditCard, RefreshCw, Search, 
  Filter, Download, ArrowLeft, Calendar, User, Package, 
  CheckCircle, XCircle, AlertCircle, RotateCcw, Eye, ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRevenue = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateRange: '30',
    packageType: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchPayments();
    fetchRevenueStats();
  }, [filters, pagination.page]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      
      const response = await fetch(`${API_URL}/api/admin/payments?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      }
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/revenue/stats?days=${filters.dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load revenue stats');
    }
  };

  const handleRefund = async () => {
    if (!selectedPayment) return;
    
    setRefundLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/payments/${selectedPayment.id}/refund`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Refund processed successfully');
        setRefundDialogOpen(false);
        setSelectedPayment(null);
        fetchPayments();
        fetchRevenueStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process refund');
      }
    } catch (error) {
      toast.error('Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-500/10 text-green-400 border-green-500/20',
      paid: 'bg-green-500/10 text-green-400 border-green-500/20',
      pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      refunded: 'bg-red-500/10 text-red-400 border-red-500/20',
      failed: 'bg-red-500/10 text-red-400 border-red-500/20'
    };
    
    return (
      <Badge className={statusStyles[status] || 'bg-gray-500/10 text-gray-400'}>
        {status?.toUpperCase() || 'UNKNOWN'}
      </Badge>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8 pt-24">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="text-muted-foreground"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
            </div>
          </div>
          <div className="mt-4">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight">
              Revenue <span className="text-primary">Management</span>
            </h1>
            <p className="text-muted-foreground mt-1">Track payments, revenue, and process refunds</p>
          </div>
        </motion.div>

        {/* Revenue Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="font-mono text-2xl font-bold mt-1 text-green-400">
                    {formatCurrency(stats?.total_revenue)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Last {filters.dateRange} days</p>
                </div>
                <div className="w-12 h-12 rounded bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Transactions</p>
                  <p className="font-mono text-2xl font-bold mt-1">{stats?.total_transactions || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Completed payments</p>
                </div>
                <div className="w-12 h-12 rounded bg-blue-500/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg. Transaction</p>
                  <p className="font-mono text-2xl font-bold mt-1">
                    {formatCurrency(stats?.average_transaction)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Per purchase</p>
                </div>
                <div className="w-12 h-12 rounded bg-purple-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Refunds</p>
                  <p className="font-mono text-2xl font-bold mt-1 text-red-400">
                    {formatCurrency(stats?.total_refunds)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{stats?.refund_count || 0} refunds</p>
                </div>
                <div className="w-12 h-12 rounded bg-red-500/10 flex items-center justify-center">
                  <RotateCcw className="w-6 h-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Revenue by Package Type */}
        {stats?.revenue_by_package && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Revenue by Package Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(stats.revenue_by_package).map(([type, data]) => (
                    <div key={type} className="bg-background/50 rounded-lg p-4 border border-border">
                      <p className="text-sm text-muted-foreground capitalize">{type} Challenges</p>
                      <p className="font-mono text-xl font-bold mt-1">{formatCurrency(data.revenue)}</p>
                      <p className="text-xs text-muted-foreground">{data.count} sales</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by email, name, or payment ID..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, dateRange: value }))}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="365">Last year</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.packageType}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, packageType: value }))}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Package" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Packages</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="instant">Instant</SelectItem>
                    <SelectItem value="swing">Swing</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" onClick={fetchPayments}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Payments Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment History
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {pagination.total} total payments
                </p>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No payments found matching your filters</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Package</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell className="whitespace-nowrap">
                              {formatDate(payment.created_at)}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{payment.user_name || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground">{payment.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{payment.package_name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{payment.package_type}</p>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono font-bold">
                              {formatCurrency(payment.amount)}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(payment.status)}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {payment.stripe_payment_id ? 
                                payment.stripe_payment_id.substring(0, 20) + '...' : 
                                '-'
                              }
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedPayment(payment);
                                    setDetailsDialogOpen(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {payment.status === 'completed' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:text-red-300"
                                    onClick={() => {
                                      setSelectedPayment(payment);
                                      setRefundDialogOpen(true);
                                    }}
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                      <p className="text-sm text-muted-foreground">
                        Page {pagination.page} of {totalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page === 1}
                          onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page >= totalPages}
                          onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Payment Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>
              Full details for this transaction
            </DialogDescription>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Payment ID</p>
                  <p className="font-mono text-sm break-all">{selectedPayment.id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(selectedPayment.status)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="font-mono font-bold text-lg">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="text-sm">{formatDate(selectedPayment.created_at)}</p>
                </div>
              </div>
              
              <div className="border-t border-border pt-4">
                <p className="text-sm text-muted-foreground mb-2">Customer</p>
                <div className="bg-background/50 rounded-lg p-3">
                  <p className="font-medium">{selectedPayment.user_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedPayment.user_email}</p>
                  <p className="text-xs text-muted-foreground mt-1">User ID: {selectedPayment.user_id}</p>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-sm text-muted-foreground mb-2">Package</p>
                <div className="bg-background/50 rounded-lg p-3">
                  <p className="font-medium">{selectedPayment.package_name}</p>
                  <p className="text-sm text-muted-foreground capitalize">Type: {selectedPayment.package_type}</p>
                  <p className="text-xs text-muted-foreground mt-1">Challenge ID: {selectedPayment.challenge_id}</p>
                </div>
              </div>

              {selectedPayment.stripe_payment_id && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground mb-2">Stripe Details</p>
                  <div className="bg-background/50 rounded-lg p-3">
                    <p className="text-xs font-mono break-all">{selectedPayment.stripe_payment_id}</p>
                    {selectedPayment.stripe_session_id && (
                      <p className="text-xs font-mono break-all mt-1 text-muted-foreground">
                        Session: {selectedPayment.stripe_session_id}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {selectedPayment.refunded_at && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground mb-2">Refund Info</p>
                  <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                    <p className="text-red-400 font-medium">Refunded</p>
                    <p className="text-xs text-muted-foreground">{formatDate(selectedPayment.refunded_at)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Confirmation Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-400">Process Refund</DialogTitle>
            <DialogDescription>
              Are you sure you want to refund this payment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedPayment && (
            <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p className="text-muted-foreground">Customer:</p>
                <p>{selectedPayment.user_email}</p>
                <p className="text-muted-foreground">Package:</p>
                <p>{selectedPayment.package_name}</p>
                <p className="text-muted-foreground">Amount:</p>
                <p className="font-mono font-bold text-red-400">{formatCurrency(selectedPayment.amount)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRefund}
              disabled={refundLoading}
            >
              {refundLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Process Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRevenue;
